import { AppStore } from '../state/store';
import { bindPanelCollapse, panelEnd, panelStart } from './collapsiblePanel';
import { sidebarPane } from './sidebarTabs';

export interface TrainPanelCallbacks {
  onSimEnabledChange(enabled: boolean): void;
  onCameraChange(cam: 'orbit' | 'pov'): void;
}

/** Run/view toggles plus car count, masses, friction, drag, minimum train speed. */
export class TrainPanel {
  /** Train keeps running while checked — default on for first load. */
  simEnabled = true;
  /** Seat camera locked to the front car. */
  viewTrain = false;

  private el: HTMLElement;
  private simCheck: HTMLInputElement | null = null;
  private viewTrainCheck: HTMLInputElement | null = null;

  constructor(
    private store: AppStore,
    private cb: TrainPanelCallbacks,
  ) {
    this.el = document.createElement('div');
    sidebarPane('simulate').appendChild(this.el);
    store.on('doc', () => this.render());
    this.render();
  }

  /** Toggle on-ride seat camera; no-ops when Simulate is off. */
  setViewTrain(on: boolean): void {
    const next = on && this.simEnabled;
    if (this.viewTrain === next) {
      if (this.viewTrainCheck) this.viewTrainCheck.checked = next;
      this.syncViewTrainEnabled();
      return;
    }
    this.viewTrain = next;
    if (this.viewTrainCheck) this.viewTrainCheck.checked = next;
    this.syncViewTrainEnabled();
    this.cb.onCameraChange(next ? 'pov' : 'orbit');
  }

  private syncViewTrainEnabled(): void {
    if (this.viewTrainCheck) this.viewTrainCheck.disabled = !this.simEnabled;
  }

  private setSimEnabled(enabled: boolean): void {
    this.simEnabled = enabled;
    if (!this.simEnabled) this.setViewTrain(false);
    else this.syncViewTrainEnabled();
    this.cb.onSimEnabledChange(this.simEnabled);
  }

  private render(): void {
    const { train, settings } = this.store.doc;
    this.el.innerHTML = `
      ${panelStart('train', 'Train & physics')}
        <div class="row"><label title="Run the train continuously while editing"><input type="checkbox" data-act="sim"${this.simEnabled ? ' checked' : ''}> Simulate</label></div>
        <div class="row"><label title="Camera from the front seat of the train (requires Simulate)"><input type="checkbox" data-act="view"${this.viewTrain ? ' checked' : ''}${this.simEnabled ? '' : ' disabled'}> View train</label></div>
        ${slider('cars', 'Cars', train.cars, 1, 8, 1)}
        ${slider('carMass', 'Car mass (kg)', train.carMass, 400, 1600, 50)}
        ${slider('friction', 'Friction μ', settings.friction, 0, 0.05, 0.002)}
        ${slider('drag', 'Drag k', settings.drag, 0, 0.01, 0.0005)}
        ${slider('minSpeed', 'Min speed (m/s)', settings.minSpeed, 0.5, 20, 0.1)}
      ${panelEnd()}`;

    bindPanelCollapse(this.el);

    this.simCheck = this.el.querySelector<HTMLInputElement>('[data-act="sim"]');
    this.viewTrainCheck = this.el.querySelector<HTMLInputElement>('[data-act="view"]');
    this.simCheck?.addEventListener('change', () => this.setSimEnabled(this.simCheck!.checked));
    this.viewTrainCheck?.addEventListener('change', () => this.setViewTrain(this.viewTrainCheck!.checked));

    this.el.querySelectorAll<HTMLInputElement>('input[type="range"]').forEach((input) => {
      const valEl = input.parentElement!.querySelector('.val')!;
      input.addEventListener('input', () => {
        valEl.textContent = fmt(parseFloat(input.value));
      });
      input.addEventListener('change', () => {
        const key = input.dataset.key!;
        const value = parseFloat(input.value);
        this.store.commit((d) => {
          if (key === 'cars') d.train.cars = Math.round(value);
          else if (key === 'carMass') d.train.carMass = value;
          else if (key === 'friction') d.settings.friction = value;
          else if (key === 'drag') d.settings.drag = value;
          else if (key === 'minSpeed') d.settings.minSpeed = value;
        });
        this.store.emit('train');
      });
    });
  }
}

function slider(key: string, label: string, value: number, min: number, max: number, step: number): string {
  return `<div class="row"><label>${label}</label>
    <input type="range" data-key="${key}" min="${min}" max="${max}" step="${step}" value="${value}">
    <span class="val">${fmt(value)}</span></div>`;
}

function fmt(v: number): string {
  return v >= 10 ? v.toFixed(0) : v >= 1 ? v.toFixed(1) : v.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
}
